/**
 * Single source of truth for which environment (Dev or Prod) the web app targets.
 *
 * Everything env-specific — the Supabase project, the share domain, and the DEV
 * banner — derives from ONE resolved environment, so they can never disagree.
 *
 * Resolution order (highest priority first):
 *   1. Canonical host — if the page is served from a known custom domain, that
 *      domain's environment is AUTHORITATIVE and cannot be overridden. This is the
 *      hard guarantee that share.previspro.com (Prod) can never serve Dev config —
 *      regardless of how the bundle was built or which Build Command Vercel ran.
 *      share-dev.previspro.com -> Dev.
 *   2. VITE_ENV_NAME — the build-time signal, used where the host isn't canonical:
 *      embedded (file://, set by Unity's Build Timeline WebApp per PREVIS_PROD_ENV),
 *      local dev (localhost, .env.development), and Vercel preview URLs.
 *   3. Default 'dev' — fail safe. An unknown context is never silently treated as Prod.
 *
 * The Supabase anon keys below are PUBLIC client keys (protected by RLS, already
 * committed in .env.dev-deploy / .env.prod-deploy). Baking both configs in lets a
 * SINGLE bundle serve the correct backend by host, which is what removes the entire
 * "wrong build deployed to the wrong domain" failure class for the standalone deploy.
 */

export type EnvName = 'dev' | 'prod';

export interface PrevisEnv {
  readonly name: EnvName;
  readonly supabaseUrl: string;
  readonly supabaseAnonKey: string;
  readonly shareDomain: string;
  readonly isDev: boolean;
}

const CONFIGS: Record<EnvName, Omit<PrevisEnv, 'isDev'>> = {
  dev: {
    name: 'dev',
    supabaseUrl: 'https://fhkvhrxpmtswehbwfhnh.supabase.co',
    supabaseAnonKey: 'sb_publishable_KfOadojJNa5Zb2Hf9iESQQ_OPr2VJbK',
    shareDomain: 'share-dev.previspro.com',
  },
  prod: {
    name: 'prod',
    supabaseUrl: 'https://ugyyoynzryunuwcweinn.supabase.co',
    supabaseAnonKey:
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVneXlveW56cnl1bnV3Y3dlaW5uIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA5MjQxNTksImV4cCI6MjA4NjUwMDE1OX0.XMJUV3IWsVlxKRd3uOhZgJpza6ZDiy8PwL4YGjik1G0',
    shareDomain: 'share.previspro.com',
  },
};

/**
 * Custom domains where the served host unambiguously determines the environment.
 * The host wins over any build-time signal, so a misbuilt bundle can't run as the
 * wrong environment here. Add new production/review domains to this map.
 */
const CANONICAL_HOSTS: Readonly<Record<string, EnvName>> = {
  'share.previspro.com': 'prod',
  'share-dev.previspro.com': 'dev',
};

/** Environment forced by the canonical custom domain, or null if the host isn't one. */
function hostEnvName(): EnvName | null {
  if (typeof window === 'undefined' || !window.location) return null;
  const host = window.location.hostname.toLowerCase().replace(/^www\./, '');
  return CANONICAL_HOSTS[host] ?? null;
}

/** Environment baked into the bundle at build time via VITE_ENV_NAME, or null. */
function buildEnvName(): EnvName | null {
  const raw = import.meta.env.VITE_ENV_NAME as string | undefined;
  if (raw === 'dev' || raw === 'prod') return raw;
  if (raw != null && raw !== '') {
    console.error(`[previs-env] VITE_ENV_NAME must be 'dev' or 'prod', got: ${raw}`);
  }
  return null;
}

function resolveEnv(): PrevisEnv {
  const fromHost = hostEnvName();
  const fromBuild = buildEnvName();

  // Canonical host wins — the hard guarantee that the Prod domain is always Prod.
  const name: EnvName = fromHost ?? fromBuild ?? 'dev';

  // Detectability: if the bundle was built for one env but is served from a canonical
  // host for the other, the host wins but we shout so the build misconfiguration still
  // gets fixed. This is exactly the case that once shipped a Dev bundle to Prod silently.
  if (fromHost && fromBuild && fromHost !== fromBuild) {
    console.error(
      `[previs-env] Bundle was built for '${fromBuild}' but is served from a '${fromHost}' ` +
        `host (${window.location.hostname}). Forcing '${fromHost}'. ` +
        `Fix this Vercel project's build config so the bundle matches the domain.`,
    );
  }

  return { ...CONFIGS[name], isDev: name === 'dev' };
}

/**
 * Typed accessor for environment-specific config. Resolved once at module load.
 *   npm run dev          -> localhost      -> VITE_ENV_NAME=dev   (.env.development)
 *   npm run build        -> dev-deploy     -> VITE_ENV_NAME=dev   (.env.dev-deploy)
 *   npm run build:prod   -> prod-deploy    -> VITE_ENV_NAME=prod  (.env.prod-deploy)
 *   served on share.previspro.com          -> ALWAYS prod (host authoritative)
 *   served on share-dev.previspro.com      -> ALWAYS dev  (host authoritative)
 */
export const env: PrevisEnv = resolveEnv();
